import { useMediaQuery, useTheme } from '@mui/material';

/**
 * Shared column grid for the Add-ons, Other Items and Deductions tables so their columns line up
 * when stacked. Percentages must sum to 100 with the action column included.
 */
export const ACTUALIZATION_COLUMN_WIDTHS = {
  name: '19%',
  qty: '12%',
  pricing: '14%',
  discount: '10%',
  tax: '14%',
  amount: '12%',
  variance: '10%',
  action: '9%',
};

const sumWidths = (...widths: string[]) => `${widths.reduce((sum, w) => sum + parseFloat(w), 0)}%`;

/** Without a Tax column (A&G), Discount absorbs the Tax width so Amount onwards still line up. */
export const lineItemColumnWidths = (pastBooking: boolean, hasTax = true) => {
  const w = ACTUALIZATION_COLUMN_WIDTHS;
  const middle = hasTax ? [w.discount, w.tax] : [sumWidths(w.discount, w.tax)];
  return [w.name, w.qty, w.pricing, ...middle, w.amount, w.variance, ...(pastBooking ? [w.action] : [])];
};

/** Deductions merge Name..Discount into Description so Deduction Type sits in the Tax column. */
export const deductionColumnWidths = (pastBooking: boolean) => {
  const w = ACTUALIZATION_COLUMN_WIDTHS;
  return [sumWidths(w.name, w.qty, w.pricing, w.discount), w.tax, w.amount, w.variance, ...(pastBooking ? [w.action] : [])];
};

/** Phones show only Name, Qty, Amount and Actions. */
export const useActualizationMobile = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('sm'));
};

export const mobileLineItemColumnWidths = (pastBooking: boolean) =>
  pastBooking ? ['38%', '18%', '26%', '18%'] : ['46%', '22%', '32%'];

/** Deductions have no Qty, so on phones only Description, Amount and Actions remain. */
export const mobileDeductionColumnWidths = (pastBooking: boolean) =>
  pastBooking ? ['56%', '26%', '18%'] : ['66%', '34%'];

export const actualizationTableSxFor = (mobile: boolean) =>
  mobile ? { ...actualizationTableSx, minWidth: 0 } : actualizationTableSx;

export const actualizationTableSx = {
  tableLayout: 'fixed',
  minWidth: 760,
  'thead > tr': { whiteSpace: 'nowrap', height: 36 },
  'tbody > tr': { height: 'auto' },
  'thead .MuiTableCell-root': {
    py: 0.5,
    px: 1,
    height: 36,
    boxSizing: 'border-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  'tbody .MuiTableCell-root': {
    py: 0.75,
    px: 1,
    height: 'auto',
    fontSize: '12px',
    lineHeight: 1.25,
    verticalAlign: 'middle',
    boxSizing: 'border-box',
    overflowWrap: 'anywhere',
  },
  'tbody .MuiTableCell-root:has(.actualization-delete-btn)': { whiteSpace: 'nowrap' },
};
